import { FC } from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import { RootState } from "../../../store";
import { BO_CORE_DEFAULT_VALUE } from "../../../utils/AppUtils";
import OneHealthWordmark from "../../../components/OneHealthWordmark/OneHealthWordmark";

interface PublicPageLogoProps {
    /** Cap on a tenant logo's rendered height. The image is contained, so wide logos shrink to fit. */
    maxHeight?: number;
}

/**
 * Width of the 1health wordmark, ~a third of the card — the proportion the design mockup shows.
 * Sized by width rather than height because it is a 4.8:1 lockup, not a square mark like a tenant logo.
 */
const WORDMARK_WIDTH = 150;

/**
 * The mark at the top of a public page's card (NG-8606).
 *
 * A tenant's own logo when they have set one, and the 1health wordmark otherwise — the design mockup
 * shows the card leading with a logo, and a card with an empty space above the heading reads as a
 * loading state rather than a deliberate layout.
 *
 * This also replaces the five-div padding-bottom aspect-ratio hack that was copy-pasted into Login,
 * Authorized and UpdatePassword. `object-fit: contain` inside a fixed-height box does the same job
 * — it reserves the space so the card does not jump when the image loads.
 *
 * The backend uses a sentinel rather than null for "not set", hence the BO_CORE_DEFAULT_VALUE guard.
 */
const PublicPageLogo: FC<PublicPageLogoProps> = ({ maxHeight = 48 }) => {
    const tenant = useSelector((store: RootState) => store.uiSliceReducer.publicTenantData);
    const logoUrl = tenant?.publicUrlLogo;

    if (!logoUrl || logoUrl === BO_CORE_DEFAULT_VALUE) {
        return <OneHealthWordmark width={WORDMARK_WIDTH} />;
    }

    return (
        <Box
            component="img"
            src={logoUrl}
            alt={tenant?.tenantName}
            sx={{
                display: "block",
                height: maxHeight,
                maxWidth: "100%",
                objectFit: "contain",
                objectPosition: "center",
            }}
        />
    );
};

export default PublicPageLogo;
